/**
 * AgregarRecursoPresupuestoModal
 * Modal para agregar recursos al presupuesto
 * - Permite seleccionar recursos del catálogo maestro por partida
 * - Permite crear un nuevo recurso directamente en el maestro
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiPlus, FiSearch, FiCheck } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import '../styles/modals.css';
import './AgregarRecursoPresupuestoModal.css';

import Loading from './Loading';
const AgregarRecursoPresupuestoModal = ({
  isOpen,
  onClose,
  partidaId,
  tipo = 'venta',
  onSuccess
}) => {
  const [mode, setMode] = useState('select'); // 'select' o 'create'
  const [maestroRecursos, setMaestroRecursos] = useState([]);
  const [filteredRecursos, setFilteredRecursos] = useState([]);
  const [selectedRecursos, setSelectedRecursos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    cantidad_presupuesto: '',
    precio_unitario_costo: '',
    precio_unitario_venta: '',
    markup: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (isOpen && partidaId) {
      setMode('select');
      setSelectedRecursos([]);
      setSearchTerm('');
      setFormData({
        nombre: '',
        descripcion: '',
        cantidad_presupuesto: '',
        precio_unitario_costo: '',
        precio_unitario_venta: '',
        markup: ''
      });
      loadMaestroRecursos();
    }
  }, [isOpen, partidaId]);

  useEffect(() => {
    // Filter recursos based on search term
    if (searchTerm.trim() === '') {
      setFilteredRecursos(maestroRecursos);
    } else {
      const filtered = maestroRecursos.filter(recurso =>
        recurso.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (recurso.descripcion && recurso.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredRecursos(filtered);
    }
  }, [searchTerm, maestroRecursos]);

  const loadMaestroRecursos = async () => {
    setLoading(true);
    try {
      // Load all recursos from the master catalog (now independent, not grouped by partida)
      const response = await presupuestoHierarchyService.getMaestroRecursos();
      const recursos = response.data.results || response.data;
      setMaestroRecursos(recursos);
      setFilteredRecursos(recursos);
    } catch (error) {
      console.error('Error loading maestro recursos:', error);
      toast.error('Error al cargar los recursos del catálogo');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecursoSelection = (recursoId) => {
    if (selectedRecursos.includes(recursoId)) {
      setSelectedRecursos(selectedRecursos.filter(id => id !== recursoId));
    } else {
      setSelectedRecursos([...selectedRecursos, recursoId]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculatePrecioVenta = () => {
    const costo = parseFloat(formData.precio_unitario_costo) || 0;
    const markup = parseFloat(formData.markup) || 0;
    return (costo * (1 + markup / 100)).toFixed(2);
  };

  const calculateSubtotal = () => {
    const cantidad = parseFloat(formData.cantidad_presupuesto) || 0;
    const precio = parseFloat(formData.precio_unitario_venta) || 0;
    return (cantidad * precio).toFixed(2);
  };

  const handleAssignRecursos = async () => {
    if (selectedRecursos.length === 0) {
      toast.warning('Selecciona al menos un recurso');
      return;
    }

    setCreating(true);
    try {
      // Assign selected recursos to the partida in presupuesto
      const requests = selectedRecursos.map(recursoId => {
        const recurso = maestroRecursos.find(r => r.id === recursoId);
        if (tipo === 'venta') {
          return presupuestoHierarchyService.createVentaRecurso({
            partida: partidaId,
            recurso_referencia: recursoId,
            nombre: recurso?.nombre,
            descripcion: recurso?.descripcion,
            cantidad_presupuesto: recurso?.cantidad_presupuesto || 0,
            precio_unitario_costo: recurso?.precio_unitario_costo || 0,
            precio_unitario_venta: recurso?.precio_unitario_venta || 0,
            markup: recurso?.markup || 0
          });
        } else {
          return presupuestoHierarchyService.createCostoRecurso({
            partida: partidaId,
            recurso_referencia: recursoId,
            nombre: recurso?.nombre,
            descripcion: recurso?.descripcion,
            cantidad_presupuesto: recurso?.cantidad_presupuesto || 0,
            precio_unitario_costo: recurso?.precio_unitario_costo || 0,
            precio_unitario_venta: recurso?.precio_unitario_venta || 0,
            markup: recurso?.markup || 0
          });
        }
      });

      await Promise.all(requests);

      toast.success(`${selectedRecursos.length} recurso(s) asignado(s) exitosamente`);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning recursos:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar los recursos');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateRecurso = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.warning('El nombre del recurso es requerido');
      return;
    }

    if (!formData.cantidad_presupuesto || parseFloat(formData.cantidad_presupuesto) <= 0) {
      toast.warning('La cantidad debe ser mayor a 0');
      return;
    }

    if (!formData.precio_unitario_venta || parseFloat(formData.precio_unitario_venta) < 0) {
      toast.warning('El precio de venta es requerido y debe ser mayor o igual a 0');
      return;
    }

    setCreating(true);
    try {
      // Create new recurso in the master catalog
      const newRecursoResponse = await presupuestoHierarchyService.createMaestroRecurso({
        partida: partidaId,
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        cantidad_presupuesto: parseFloat(formData.cantidad_presupuesto),
        precio_unitario_costo: parseFloat(formData.precio_unitario_costo) || 0,
        precio_unitario_venta: parseFloat(formData.precio_unitario_venta),
        markup: parseFloat(formData.markup) || 0,
        status: 'activo'
      });

      const newRecursoId = newRecursoResponse.data.id;

      // Assign the new recurso to the partida in presupuesto
      if (tipo === 'venta') {
        await presupuestoHierarchyService.createVentaRecurso({
          partida: partidaId,
          recurso_referencia: newRecursoId,
          nombre: formData.nombre.trim(),
          descripcion: formData.descripcion.trim(),
          cantidad_presupuesto: parseFloat(formData.cantidad_presupuesto),
          precio_unitario_costo: parseFloat(formData.precio_unitario_costo) || 0,
          precio_unitario_venta: parseFloat(formData.precio_unitario_venta),
          markup: parseFloat(formData.markup) || 0
        });
      } else {
        await presupuestoHierarchyService.createCostoRecurso({
          partida: partidaId,
          recurso_referencia: newRecursoId,
          nombre: formData.nombre.trim(),
          descripcion: formData.descripcion.trim(),
          cantidad_presupuesto: parseFloat(formData.cantidad_presupuesto),
          precio_unitario_costo: parseFloat(formData.precio_unitario_costo) || 0,
          precio_unitario_venta: parseFloat(formData.precio_unitario_venta),
          markup: parseFloat(formData.markup) || 0
        });
      }

      toast.success('Recurso creado y asignado exitosamente');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating recurso:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el recurso');
    } finally {
      setCreating(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl agregar-recurso-presupuesto-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Agregar Recurso al Presupuesto</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <div className="mode-tabs">
            <button
              className={`mode-tab ${mode === 'select' ? 'active' : ''}`}
              onClick={() => setMode('select')}
            >
              <FiSearch /> Seleccionar del Catálogo
            </button>
            <button
              className={`mode-tab ${mode === 'create' ? 'active' : ''}`}
              onClick={() => setMode('create')}
            >
              <FiPlus /> Crear Nuevo Recurso
            </button>
          </div>

          {mode === 'select' && (
            <div className="select-recursos-section">
              <div className="info-message">
                <p>Selecciona uno o más recursos del catálogo maestro para agregar a esta partida del presupuesto.</p>
              </div>

              {/* Search bar */}
              <div className="search-bar">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar recursos por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Recursos list */}
              <div className="recursos-list">
                {loading ? (
                  <Loading fullScreen={false} size="sm" mensaje="Cargando recursos" />
                ) : filteredRecursos.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? 'No se encontraron recursos con ese criterio' : 'No hay recursos en el catálogo maestro para esta partida'}
                  </div>
                ) : (
                  filteredRecursos.map(recurso => (
                    <div
                      key={recurso.id}
                      className={`recurso-item ${selectedRecursos.includes(recurso.id) ? 'selected' : ''}`}
                      onClick={() => toggleRecursoSelection(recurso.id)}
                    >
                      <div className="recurso-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedRecursos.includes(recurso.id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="recurso-info">
                        <h4>{recurso.nombre}</h4>
                        {recurso.descripcion && <p>{recurso.descripcion}</p>}
                        <div className="recurso-details-row">
                          {recurso.cantidad_presupuesto && (
                            <span className="recurso-detail">Cantidad: {parseFloat(recurso.cantidad_presupuesto).toLocaleString('es-CL')}</span>
                          )}
                          {recurso.precio_unitario_costo && (
                            <span className="recurso-detail">Costo: ${parseFloat(recurso.precio_unitario_costo).toLocaleString('es-CL', {maximumFractionDigits: 0})}</span>
                          )}
                          {recurso.precio_unitario_venta && (
                            <span className="recurso-detail">Venta: ${parseFloat(recurso.precio_unitario_venta).toLocaleString('es-CL', {maximumFractionDigits: 0})}</span>
                          )}
                          {recurso.markup && (
                            <span className="recurso-detail">Markup: {parseFloat(recurso.markup).toLocaleString('es-CL', {maximumFractionDigits: 2})}%</span>
                          )}
                        </div>
                      </div>
                      {selectedRecursos.includes(recurso.id) && (
                        <FiCheck className="check-icon" />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Selected count */}
              {selectedRecursos.length > 0 && (
                <div className="selection-summary">
                  {selectedRecursos.length} recurso(s) seleccionado(s)
                </div>
              )}
            </div>
          )}

          {mode === 'create' && (
            <form onSubmit={handleCreateRecurso} className="recurso-form">
              <div className="form-section">
                <div className="info-message">
                  <p>Crea un nuevo recurso en el catálogo maestro. Este recurso estará disponible para todos los presupuestos y se asignará automáticamente a esta partida del presupuesto.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="nombre">
                    Nombre del Recurso <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Diseño de portada"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion">Descripción</label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Descripción opcional del recurso"
                    rows="2"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cantidad_presupuesto">
                      Cantidad <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="cantidad_presupuesto"
                      name="cantidad_presupuesto"
                      value={formData.cantidad_presupuesto}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="precio_unitario_venta">
                      Precio Venta (CLP) <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="precio_unitario_venta"
                      name="precio_unitario_venta"
                      value={formData.precio_unitario_venta}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="precio_unitario_costo">Precio Costo (CLP)</label>
                    <input
                      type="number"
                      id="precio_unitario_costo"
                      name="precio_unitario_costo"
                      value={formData.precio_unitario_costo}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="markup">Markup (%)</label>
                    <input
                      type="number"
                      id="markup"
                      name="markup"
                      value={formData.markup}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                {formData.cantidad_presupuesto && formData.precio_unitario_venta && (
                  <div className="subtotal-preview">
                    <div className="preview-row">
                      <span>Precio Venta Calculado:</span>
                      <span className="precio-venta">${calculatePrecioVenta()}</span>
                    </div>
                    <div className="preview-row">
                      <span>Subtotal Estimado:</span>
                      <span className="subtotal-value">${calculateSubtotal()}</span>
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={creating}
          >
            Cancelar
          </button>
          {mode === 'select' ? (
            <button
              className="btn btn-primary"
              onClick={handleAssignRecursos}
              disabled={creating || selectedRecursos.length === 0}
            >
              {creating ? 'Asignando...' : (
                <>
                  <FiPlus /> Asignar Recursos ({selectedRecursos.length})
                </>
              )}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleCreateRecurso}
              disabled={creating || !formData.nombre.trim()}
            >
              {creating ? 'Creando...' : (
                <>
                  <FiPlus /> Crear y Asignar Recurso
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('modal-root'));
};

export default AgregarRecursoPresupuestoModal;
